import { Check, ExternalLink, Loader2, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useConfigurationProfiles } from '@/hooks/commercial/use-configuration-profiles';
import { useSaveProposalConfiguration } from '@/hooks/commercial/use-save-proposal-configuration';
import type {
  AvailableConfigurationProfile,
  SelectedProfile,
} from '@/types/commercial/proposal-configurations';

interface StepConfigurationModalProps {
  open: boolean;
  onClose: () => void;
  proposalId?: string;
  groupId?: string;
  selectedProfiles?: SelectedProfile[];
  totalDevices?: number;
}

interface ProfileSelection {
  profile: AvailableConfigurationProfile;
  quantity: number;
}

const CREATE_PROFILE_PATH = '/production/configuration-profile/form/create';
const getProfileDetailPath = (profileId: string) =>
  `/production/configuration-profile/form/update?id=${profileId}`;

export function StepConfigurationModal({
  open,
  onClose,
  proposalId,
  groupId,
  selectedProfiles = [],
  totalDevices = 0,
}: StepConfigurationModalProps) {
  const [selections, setSelections] = useState<ProfileSelection[]>([]);
  const saveConfiguration = useSaveProposalConfiguration(proposalId);

  const {
    data: profiles,
    isError,
    isLoading,
    refetch,
  } = useConfigurationProfiles(open ? proposalId : undefined, open ? groupId : undefined);

  useEffect(() => {
    if (!open || !profiles) return;

    setSelections(
      selectedProfiles
        .map((selected) => {
          const profile = profiles.find((item) => item.id === selected.id);
          if (!profile) return null;
          return { profile, quantity: selected.quantity };
        })
        .filter((selection): selection is ProfileSelection => selection !== null),
    );
  }, [open, profiles, selectedProfiles]);

  const resetAndClose = () => {
    setSelections([]);
    onClose();
  };

  const toggleProfile = (profile: AvailableConfigurationProfile) => {
    setSelections((current) => {
      if (current.some((selection) => selection.profile.id === profile.id)) {
        return current.filter((selection) => selection.profile.id !== profile.id);
      }
      return [...current, { profile, quantity: 1 }];
    });
  };

  const updateQuantity = (profileId: string, quantity: number) => {
    setSelections((current) =>
      current.map((selection) =>
        selection.profile.id === profileId
          ? { ...selection, quantity: Math.max(1, quantity) }
          : selection,
      ),
    );
  };

  const totalSelected = selections.reduce((sum, selection) => sum + selection.quantity, 0);
  const canSave = selections.length > 0 && totalSelected <= totalDevices;

  const handleSave = async () => {
    if (!proposalId || !groupId || !canSave) return;

    try {
      await saveConfiguration.mutateAsync({
        groupId,
        profiles: selections.map((selection) => ({
          profileId: selection.profile.id,
          quantity: selection.quantity,
        })),
      });
      resetAndClose();
    } catch {
      return;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && resetAndClose()}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Perfis de Configuração</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Total de dispositivos: {totalDevices} | Selecionados: {totalSelected}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => window.open(CREATE_PROFILE_PATH, '_blank')}
            >
              <Plus className="h-3.5 w-3.5" />
              Criar Perfil
            </Button>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && isError && (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <p className="text-sm text-muted-foreground">Erro ao carregar perfis.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}

          {!isLoading && !isError && (!profiles || profiles.length === 0) && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Nenhum perfil disponível encontrado.
            </p>
          )}

          {!isLoading && !isError && profiles && profiles.length > 0 && (
            <div className="space-y-2">
              {profiles.map((profile) => {
                const selection = selections.find((item) => item.profile.id === profile.id);
                const isSelected = !!selection;

                return (
                  <button
                    type="button"
                    key={profile.id}
                    className={`flex w-full cursor-pointer items-center justify-between rounded-md border p-3 text-left transition-colors ${
                      isSelected ? 'border-blue-500 bg-blue-50/50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleProfile(profile)}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                          isSelected ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{profile.name}</p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <Badge variant="secondary-subtle" className="text-[10px]">
                            {profile.type}
                          </Badge>
                          {profile.technology && (
                            <span className="text-xs text-muted-foreground">
                              {profile.technology.name}
                            </span>
                          )}
                          {profile.status && (
                            <Badge
                              variant={
                                profile.status === 'ATIVO' ? 'success-subtle' : 'warning-subtle'
                              }
                              className="text-[10px]"
                            >
                              {profile.status === 'ATIVO'
                                ? 'Ativo'
                                : profile.status === 'AGUARDANDO_VALIDACAO'
                                  ? 'Aguardando Validação'
                                  : profile.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(getProfileDetailPath(profile.id), '_blank');
                        }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    {isSelected && (
                      <div className="ml-3 flex shrink-0 items-center gap-2">
                        <span className="text-xs text-muted-foreground">Qtd:</span>
                        <input
                          type="number"
                          min={1}
                          max={totalDevices}
                          value={selection?.quantity ?? 1}
                          className="w-16 rounded border px-2 py-1 text-center text-sm"
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) =>
                            updateQuantity(profile.id, Number(event.target.value))
                          }
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {totalSelected > totalDevices && (
            <p className="text-xs text-destructive">
              Total selecionado ({totalSelected}) excede o total de dispositivos ({totalDevices})
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || saveConfiguration.isPending}
            className="bg-black text-white hover:bg-gray-800"
          >
            {saveConfiguration.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Confirmar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
